<template>
  <!-- persistent: a stray click outside the dialog must not silently reject
       the deployment — only the explicit Cancel button does. -->
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    persistent
    scrollable
    content-class="deployment-dialog"
    @update:model-value="onDialogToggle"
  >
    <v-card>
      <v-card-title class="text-h6 px-4 pt-4">
        Deploy Task Set Profile
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-1">
          <strong>{{ profileCode }}</strong> | {{ profileTitle }}
          <v-chip size="small" color="green" label class="ml-2">Published</v-chip>
        </p>
        <p class="text-body-2 text-medium-emphasis mb-4">
          Schedule: {{ scheduleTitle }}
        </p>

        <div class="d-flex justify-center mb-4">
          <v-chip color="primary" label>
            Deploy under {{ targets.length }} task{{ targets.length !== 1 ? 's' : '' }}
          </v-chip>
        </div>

        <v-list density="compact" class="deployment-target-list mb-4">
          <v-list-item v-for="target in targets" :key="target.id">
            <v-list-item-title>
              <strong>{{ target.code || target.id }}</strong> — {{ target.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list>

        <v-text-field
          v-model="localTitle"
          label="Deployment Title"
          variant="outlined"
          density="compact"
          :rules="[v => !!v || 'Deployment title is required']"
          required
        ></v-text-field>

        <v-textarea
          v-model="localMessage"
          label="Deployment Message"
          variant="outlined"
          density="compact"
        ></v-textarea>

        <p class="text-caption text-medium-emphasis">
          Deploying generates the profile's task structure under each selected
          task and links it to the profile, so published profile changes sync
          into this schedule.
        </p>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="grey" :disabled="deploying" @click="emit('cancel')">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="deploying"
          :disabled="!localTitle.trim()"
          @click="emit('confirm', { title: localTitle, message: localMessage })"
        >
          Confirm Deploy
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  profileCode: { type: String, default: '' },
  profileTitle: { type: String, default: '' },
  scheduleTitle: { type: String, default: '' },
  targets: { type: Array, default: () => [] },
  // Agent-suggested title/message — prefill the fields each time the dialog
  // opens; the user can edit before confirming.
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  deploying: { type: Boolean, default: false }
});

const emit = defineEmits(['confirm', 'cancel', 'update:modelValue']);

const localTitle = ref('');
const localMessage = ref('');

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      localTitle.value =
        props.title ||
        `Deploy ${props.profileTitle || props.profileCode} to ${props.targets.length} task${props.targets.length !== 1 ? 's' : ''}`;
      localMessage.value = props.message;
    }
  }
);

// Closing the dialog any way other than Confirm (backdrop click, Esc) is a
// rejection — the agent is awaiting a respond() either way.
const onDialogToggle = (open) => {
  if (!open) emit('cancel');
};
</script>

<style>
/* Unscoped: the dialog teleports to the overlay container outside this
   component's DOM. The app runs in an iframe the parent sizes to content, so
   the iframe viewport can be far taller than the visible screen. Vuetify
   centres the dialog in that tall viewport and caps it at calc(100% - 48px),
   which lets this card (target list + title + message) grow until the
   Cancel/Confirm actions sit below the visible screen. Anchor to the top and
   cap the height in px so the actions stay reachable; `scrollable` keeps the
   title and actions pinned and scrolls the card text. The cap goes on the
   overlay content because Vuetify's scrollable rule
   (.v-dialog--scrollable > .v-overlay__content > .v-card { max-height: 100% })
   outranks a cap on the card itself. `top` overrides the app-wide
   `.v-dialog .v-overlay__content { top: 20vh }` from WelcomeDialog, which is
   20% of the (tall) iframe, not of the screen. Same approach as
   CalendarImportDialog and the ReviewStructureStep Save Tasks dialog. */
.v-dialog > .v-overlay__content.deployment-dialog {
  top: 0 !important;
  align-self: flex-start;
  margin-top: 24px;
  max-height: min(calc(100vh - 48px), 640px);
}
</style>

<style lang="scss" scoped>
.deployment-target-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
